/**
 * Tutorial Service
 * Handles all tutorial-related API calls
 *
 * @module services/TutorialService
 */

import BaseService from "core/api/BaseService";
import { API_ENDPOINTS } from "utils/constants";

class TutorialService extends BaseService {
  constructor() {
    super(API_ENDPOINTS.TUTORIALS.BASE);
  }

  /**
   * Get tutorial content
   * @param {string} tutorialId - Tutorial ID
   * @returns {Promise} Tutorial content
   */
  async getContent(tutorialId) {
    return this.get(API_ENDPOINTS.TUTORIALS.CONTENT(tutorialId));
  }

  /**
   * Get tutorial tracks
   * @param {string} tutorialId - Tutorial ID
   * @returns {Promise} Tutorial tracks
   */
  async getTracks(tutorialId) {
    return this.get(API_ENDPOINTS.TUTORIALS.TRACKS(tutorialId));
  }

  /**
   * Purchase tutorial
   * @param {string} tutorialId - Tutorial ID
   * @returns {Promise} Purchase result
   */
  async purchase(tutorialId) {
    return this.post(API_ENDPOINTS.TUTORIALS.PURCHASE(tutorialId), {});
  }

  /**
   * Get my purchased tutorials
   * @returns {Promise} My tutorials
   */
  async getMyTutorials() {
    return this.get("my-student-tutorials");
  }


  /**
   * Mark tutorial as completed
   * @param {string} tutorialId - Tutorial ID
   * @returns {Promise} Completion result
   */
  async markCompleted(tutorialId) {
    return this.post(`${tutorialId}/complete`, {});
  }

  /**
   * Get related tutorials
   * @param {string} tutorialId - Tutorial ID
   * @returns {Promise} Related tutorials
   */
  async getRelatedTutorials(tutorialId) {
    return this.get(`${tutorialId}/related`);
  }


  /**
   * Publish tutorial (for teachers)
   * @param {string} tutorialId - Tutorial ID
   * @returns {Promise} Publish result
   */
  async publish(tutorialId) {
    return this.post(`${tutorialId}/publish`, {});
  }

  /**
   * Unpublish tutorial (for teachers)
   * @param {string} tutorialId - Tutorial ID
   * @returns {Promise} Unpublish result
   */
  async unpublish(tutorialId) {
    return this.post(`${tutorialId}/unpublish`, {});
  }

  /**
   * Get tutorial statistics (for teachers)
   * @param {string} tutorialId - Tutorial ID
   * @returns {Promise} Tutorial statistics
   */
  async getStatistics(tutorialId) {
    return this.get(`${tutorialId}/statistics`);
  }

  /**
   * Add track to tutorial
   * @param {string} tutorialId - Tutorial ID
   * @param {Object} trackData - Track data
   * @returns {Promise} Created track
   */
  async addTrack(tutorialId, trackData) {
    return this.post(`${tutorialId}/tracks`, trackData);
  }

  /**
   * Update track
   * @param {string} tutorialId - Tutorial ID
   * @param {string} trackId - Track ID
   * @param {Object} trackData - Updated track data
   * @returns {Promise} Updated track
   */
  async updateTrack(tutorialId, trackId, trackData) {
    return this.put(`${tutorialId}/tracks/${trackId}`, trackData);
  }

  /**
   * Delete track
   * @param {string} tutorialId - Tutorial ID
   * @param {string} trackId - Track ID
   * @returns {Promise} Delete result
   */
  async deleteTrack(tutorialId, trackId) {
    return this.deleteCustom(`${tutorialId}/tracks/${trackId}`);
  }
}

// Export singleton instance
export default new TutorialService();

